import { computed } from "vue";
import { toBoolean } from "#ce/utils/props";
import { isObjectLike } from "#ce/utils/types";

/**
 * The icon each item shows, the icon the activator shows, and whether that icon stands alone.
 *
 * A row's icon rides its `props.prependIcon`, which is where `SpList` reads one. The activator
 * shows the selected item's icon, or `placeholderIcon` while nothing is selected. `iconOnly`
 * reports that the activator is reduced to that icon, so it has no text of its own to name it.
 *
 * @param {object} props  Owning component's reactive props; reads `itemIcon`, `iconOnly` and
 *   `placeholderIcon`.
 * @param {{ items: import("vue").Ref<any[]>, selectedItem: import("vue").Ref<any>,
 *   multiple: import("vue").Ref<boolean> }} options
 */
export function useItemIcons(props, { items, selectedItem, multiple }) {
  function iconFor(item) {
    return isObjectLike(item) ? item[props.itemIcon] : undefined;
  }

  const listItems = computed(() =>
    items.value.map((item) => {
      const icon = iconFor(item);
      if (!icon) {
        return item;
      }

      return { ...item, props: { ...item.props, prependIcon: icon } };
    }),
  );

  const selectedIcon = computed(() => {
    if (multiple.value) {
      return undefined;
    }
    if (!selectedItem.value) {
      return props.placeholderIcon;
    }

    return iconFor(selectedItem.value);
  });

  const iconOnly = computed(() => toBoolean(props.iconOnly) && Boolean(selectedIcon.value));

  return { listItems, selectedIcon, iconOnly };
}
